import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicProviderRisk } from '@app/src/types/topicalRisk'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useMemo } from 'react'

interface UseTopicRiskStatusesResult {
  riskStatusByTemplate: Map<number, RiskStatus>
  isLoading: boolean
}

const TOPIC_RISK_INCLUDES = ['sustainabilityAreaRisks.sustainabilityTopicRisks']

export function useTopicRiskStatuses(providerId: number): UseTopicRiskStatusesResult {
  const { items = [], isLoading } = useFetchCollectionWithPost<SustainabilityTopicProviderRisk>({
    key: [FetchKey.ProviderRisk, 'profile-risk', providerId],
    endpoint: endpoints.topicalRisk,
    payload: {
      filter: [
        { name: 'providerId', filters: [{ operator: Operators.EqualTo, value: providerId }] },
        { name: 'isLatest', filters: [{ operator: Operators.EqualTo, value: true }] },
      ],
      include: TOPIC_RISK_INCLUDES,
    },
    options: { staleTime: ONE_MINUTE_MS, enabled: Boolean(providerId) },
  })

  const riskStatusByTemplate = useMemo(() => {
    const providerRisk = items[0]
    const entries = (providerRisk?.sustainabilityAreaRisks ?? []).flatMap(areaRisk =>
      (areaRisk.sustainabilityTopicRisks ?? [])
        .filter(topicRisk => topicRisk.assessmentTemplateId != null)
        .map(topicRisk => [topicRisk.assessmentTemplateId, topicRisk.totalRiskStatus] as const),
    )
    return new Map<number, RiskStatus>(entries)
  }, [items])

  return { riskStatusByTemplate, isLoading }
}
